/**
 * Machine page › Documents.
 *
 * The demo's `machineTab('sources')` (line 2242): every document in the lake
 * that mentions this machine, grouped by the system it came from, largest
 * group first. Clicking one opens the `oem-source` drawer with the lake
 * document and its provenance.
 *
 * The index (`doc-index.json` `docidx`) carries all 1,822 rows for the 100
 * machines and is a single 327 KB fetch, so the tab lists without touching the
 * 2 MB lake itself; a document's body is fetched only when it is opened.
 *
 * UI layer.
 *
 * @see src/versions/v1/chrome-oem/SourceDrawerBody.tsx — the drawer payload
 * @see docs/logic/fleet/machine-page.md
 */

import { useMemo } from 'react';
import { useLakeDoc } from '../../shared/lake-drawer';
import { useOemData } from '../../shared/useOemData';
import { TabLoader } from './TabLoader';
import { documentGroups } from './MachineScene.transform';

export interface MachineDocumentsTabProps {
  machineId: string;
  model: string;
}

export function MachineDocumentsTab({ machineId, model }: MachineDocumentsTabProps) {
  const { data, loading, error } = useOemData('doc-index');
  const openLakeDoc = useLakeDoc();

  const groups = useMemo(
    () => (data ? documentGroups(data.docidx[machineId] ?? []) : []),
    [data, machineId],
  );
  const total = useMemo(() => groups.reduce((a, g) => a + g.entries.length, 0), [groups]);

  return (
    <TabLoader loading={loading} error={error} what="The document index" rows={6}>
      {total === 0 ? (
        <p className="text-sm text-muted">No documents in the lake mention {machineId}.</p>
      ) : (
        <div className="@container space-y-4">
          <p className="text-sm text-muted">
            <b className="text-zinc-900 dark:text-zinc-100">{total}</b> documents across{' '}
            {groups.length} systems — this machine&rsquo;s own records and the documents for the{' '}
            {model}: parts-book sections, bulletins, procedures, the PM standard.
          </p>

          <div className="grid grid-cols-1 gap-4 @2xl:grid-cols-2 @5xl:grid-cols-3">
            {groups.map((group) => (
              <section
                key={group.connector}
                className="rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-3 backdrop-blur-lg"
              >
                <h3 className="text-xs uppercase tracking-wider text-muted">
                  {group.connector} · {group.entries.length}
                </h3>
                <ul className="mt-2 space-y-1">
                  {group.entries.slice(0, 14).map((entry) => (
                    <li key={entry.key} className="flex min-w-0 items-center gap-2">
                      <span className="mono shrink-0 rounded bg-zinc-100 px-1 py-0.5 text-[9px] uppercase text-muted dark:bg-zinc-800">
                        {entry.kind}
                      </span>
                      <button
                        type="button"
                        title={entry.label}
                        onClick={() =>
                          openLakeDoc({ file: entry.label, connector: group.connector })
                        }
                        className="min-w-0 flex-1 truncate text-left text-xs text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
                      >
                        {entry.label}
                      </button>
                    </li>
                  ))}
                </ul>
                {group.entries.length > 14 ? (
                  <p className="mt-1 text-[11px] text-muted">
                    +{group.entries.length - 14} more
                  </p>
                ) : null}
              </section>
            ))}
          </div>
        </div>
      )}
    </TabLoader>
  );
}
